import { RefObject, useEffect, useState } from "react";
import { isTV } from "../utils/platform";

const DEFAULT_ROOT_MARGIN = isTV ? "500px 0px" : "200px";

type ObserverEntry = {
  observer: IntersectionObserver;
  callbacks: Map<Element, (isIntersecting: boolean) => void>;
};

const observers = new Map<string, ObserverEntry>();

function getSharedObserver(rootMargin: string): ObserverEntry {
  let entry = observers.get(rootMargin);
  if (entry) return entry;

  const callbacks = new Map<Element, (isIntersecting: boolean) => void>();

  const observer = new IntersectionObserver(
    (entries) => {
      for (let i = 0; i < entries.length; i++) {
        const cb = callbacks.get(entries[i].target);
        if (cb) cb(entries[i].isIntersecting);
      }
    },
    { rootMargin },
  );

  entry = { observer, callbacks };
  observers.set(rootMargin, entry);
  return entry;
}

function observe(
  el: Element,
  rootMargin: string,
  callback: (isIntersecting: boolean) => void,
): () => void {
  const entry = getSharedObserver(rootMargin);
  entry.callbacks.set(el, callback);
  entry.observer.observe(el);

  return () => {
    entry.observer.unobserve(el);
    entry.callbacks.delete(el);

    // Tear down the shared observer when no elements remain
    if (entry.callbacks.size === 0) {
      entry.observer.disconnect();
      observers.delete(rootMargin);
    }
  };
}

export function useVisibility(
  ref: RefObject<Element | null>,
  rootMargin = DEFAULT_ROOT_MARGIN,
): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    return observe(el, rootMargin, setVisible);
  }, [ref, rootMargin]);

  return visible;
}
